import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CrossHeaderActions } from '@triedge/ai-assistant-ui';
import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from '@tabler/icons-react';
import {
  BadgeCheck,
  BookOpen,
  Bell,
  CalendarDays,
  ChevronRight,
  ClipboardCheck,
  CreditCard,
  DollarSign,
  FileUser,
  GraduationCap,
  LogOut,
  Settings,
  UserCheck,
} from 'lucide-react';
import styles from './ParentAppShell.module.css';
import type { ParentShellContentInsetProfile } from '../../app-shell/parentShellInsetProfiles';
import {
  areCognitoProfilesEquivalent,
  canUseShellThemeToggle,
  resolveAppShellPersonaLabel,
  resolveCognitoProfile,
  type CognitoProfile,
} from '../../utils/cognito-profile';
import type { SettingsNavItem } from './AppShell';
import { useNotificationsCenter } from '../../notifications/useNotificationsCenter';
import {
  CrossHeaderNotificationsMenu,
  mapCrossHeaderNotificationsToMenuItems,
  type NotificationMenuItem,
} from './CrossHeaderNotificationsMenu';
import { runBlockingLogout } from './logoutBlocking';
import {
  ASSISTANT_OVERLAY_REQUEST_CLOSE_EVENT,
  emitAssistantShellState,
} from '../../ai/assistantOverlayEvents';
import {
  OVERRIDE_GOVERNANCE_REGISTRY,
  TRIEDGE_OVERRIDE_LABELS,
  TRIEDGE_WRAPPER_LABELS,
} from '../../app-shell/overrideGovernance';
import { getAssistantRuntimePolicy } from '../../config/ai-assistant/runtime-access-policy';
import {
  PARENT_TEMP_NAV_DISCLOSURE_STORAGE_KEY,
  useTempNavDisclosure,
} from './tempNavDisclosure';
import { useYstnjNightMode } from '../../theme/useYstnjNightMode';
import { ShellThemeToggle } from './ShellThemeToggle';
import { runSafeNotificationNavigation } from './notificationNavigationSafety';
import { resolveGovernedParentPortalFallbackRoute } from '../../platform-governance/moduleGovernanceCatalog';

export type ParentPortalNavId =
  | 'dashboard'
  | 'enrollment'
  | 'applications'
  | 'financial-aid'
  | 'calendar'
  | 'tuition'
  | 'attendance'
  | 'academic'
  | 'resources'
  | 'settings';

export const PARENT_PORTAL_NAV_ITEMS: SettingsNavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: BadgeCheck },
  { id: 'applications', label: 'Applications', icon: FileUser },
  { id: 'enrollment', label: 'Enrollment', icon: UserCheck },
  { id: 'financial-aid', label: 'Financial Aid', icon: DollarSign },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays },
  { id: 'tuition', label: 'Tuition', icon: CreditCard },
  { id: 'attendance', label: 'Attendance', icon: ClipboardCheck },
  { id: 'academic', label: 'Academic', icon: GraduationCap },
  { id: 'resources', label: 'Resources', icon: BookOpen },
];

const PRIMARY_PARENT_PORTAL_NAV_ORDER = [
  'dashboard',
  'applications',
  'enrollment',
  'financial-aid',
  'tuition',
] as const;

const PRIMARY_PARENT_PORTAL_NAV_IDS = new Set<string>(PRIMARY_PARENT_PORTAL_NAV_ORDER);

export const PARENT_PORTAL_SETTINGS_ITEM: SettingsNavItem = {
  id: 'settings',
  label: 'Settings',
  icon: Settings,
};

export const PARENT_PORTAL_ROUTE_MAP: Record<ParentPortalNavId, string> = {
  // Canonical parent portal routes from Intellio-v2 route map.
  dashboard: '/parent/portal/dashboard',
  applications: '/parent/portal/applications',
  enrollment: '/parent/portal/enrollment',
  // Canonical semantic route uses a dash; we still accept the legacy no-dash form when resolving active nav.
  'financial-aid': '/parent/portal/financialaid',
  calendar: '/parent/portal/calendar',
  tuition: '/parent/portal/tuition',
  attendance: '/parent/portal/attendance',
  academic: '/parent/portal/academic',
  resources: '/parent/portal/resources',
  settings: '/parent/portal/settings',
};

const PARENT_PORTAL_TITLE_BY_NAV_ID: Record<ParentPortalNavId, string> = {
  dashboard: 'Dashboard',
  applications: 'Applications',
  enrollment: 'Enrollment',
  'financial-aid': 'Financial Aid',
  calendar: 'Portal Calendar',
  tuition: 'Tuition',
  attendance: 'Attendance',
  academic: 'Academic',
  resources: 'Resources',
  settings: 'Settings',
};

const PARENT_INTAKE_EQUIVALENCE_PATHS = [
  { intake: '/parent/portal--new/applications', active: '/parent/portal/applications' },
  { intake: '/parent/portal--new/enrollment', active: '/parent/portal/enrollment' },
  { intake: '/parent/portal--new/financialaid', active: '/parent/portal/financialaid' },
  { intake: '/parent/portal--new/financial-aid', active: '/parent/portal/financialaid' },
] as const;

const normalizeParentPortalPathname = (pathname: string): string => {
  const mappedIntakePath = (() => {
    const entry = [...PARENT_INTAKE_EQUIVALENCE_PATHS]
      .filter((candidate) => pathname === candidate.intake || pathname.startsWith(`${candidate.intake}/`))
      .sort((a, b) => b.intake.length - a.intake.length)[0];
    if (!entry) return pathname;
    const suffix = pathname.slice(entry.intake.length);
    return `${entry.active}${suffix}`;
  })();

  return mappedIntakePath
  .replace('/portal/family/parent/dashboard', '/parent/portal/dashboard')
  .replace('/portal/family/dashboard', '/parent/portal/dashboard')
  .replace('/portal/family/applications', '/parent/portal/applications')
  .replace('/portal/family/intake/applications', '/parent/portal--new/applications')
  .replace('/portal/family/enrollment', '/parent/portal/enrollment')
  .replace('/portal/family/financial-aid', '/parent/portal/financialaid')
  .replace('/portal/family/financialaid', '/parent/portal/financialaid')
  .replace('/portal/family/calendar', '/parent/portal/calendar')
  .replace('/portal/family/tuition', '/parent/portal/tuition')
  .replace('/portal/family/attendance', '/parent/portal/attendance')
  .replace('/portal/family/academic', '/parent/portal/academic')
  .replace('/portal/family/resources', '/parent/portal/resources')
  .replace('/portal/family/settings', '/parent/portal/settings');
};

export const resolveParentPortalNavId = (pathname: string): ParentPortalNavId => {
  if (pathname === '/parent/portal--new/settings' || pathname.startsWith('/parent/portal--new/settings/')) {
    return 'settings';
  }
  const normalizedPathname = normalizeParentPortalPathname(pathname);
  const entries = Object.entries(PARENT_PORTAL_ROUTE_MAP)
    .map(([id, route]) => ({ id: id as ParentPortalNavId, route }))
    .sort((a, b) => b.route.length - a.route.length);
  const match = entries.find(
    (entry) => normalizedPathname === entry.route || normalizedPathname.startsWith(`${entry.route}/`)
  );
  if (!match) return 'dashboard';
  return match.id;
};

export type ParentAppShellProps = {
  title?: string;
  subtitle?: string;
  showHeader?: boolean;
  showHeaderTitle?: boolean;
  showHeaderActions?: boolean;
  showAssistant?: boolean;
  showSecondaryNav?: boolean;
  headerPolicyReason?: string;
  contentInsetProfile?: ParentShellContentInsetProfile;
  navOpen?: boolean;
  assistantOpen?: boolean;
  lockAssistantOpen?: boolean;
  navItems?: SettingsNavItem[];
  secondaryNavItems?: SettingsNavItem[];
  settingsItem?: SettingsNavItem;
  secondaryNavLabel?: string;
  activeNavId?: string;
  contentVariant?: 'panel' | 'bleed';
  status?: React.ReactNode;
  assistant?: React.ReactNode;
  children?: React.ReactNode;
  adminChromeBanner?: React.ReactNode;
  headerAdminChrome?: React.ReactNode;
  userCardAction?: React.ReactNode;
  onNavChange?: (id: string) => void;
  onNavOpenChange?: (open: boolean) => void;
  onAssistantOpenChange?: (open: boolean) => void;
  onNotificationSelect?: (notificationId: string, route: string | null) => void | Promise<void>;
  onLogout?: () => void | Promise<void>;
  notificationMenuItems?: NotificationMenuItem[];
  notificationUnreadCount?: number;
  notificationIsLoading?: boolean;
  notificationError?: string | null;
};

const getInitials = (value?: string | null): string => {
  if (!value) return 'US';
  const cleaned = value
    .replace(/[^A-Za-z0-9\s]/g, ' ')
    .split(' ')
    .filter(Boolean);
  if (!cleaned.length) return 'US';
  if (cleaned.length === 1) return cleaned[0].slice(0, 2).toUpperCase();
  return `${cleaned[0][0]}${cleaned[cleaned.length - 1][0]}`.toUpperCase();
};

const resolveParentNotificationRoute = (
  item: NotificationMenuItem,
  availableNavItems: ReadonlyMap<string, SettingsNavItem>,
): string | null => {
  if (availableNavItems.size === 0) {
    return null;
  }

  const subject = item.text.toLowerCase();
  const applicationId = String(item.applicationId ?? '').trim();
  const encodedNotificationId = encodeURIComponent(item.id);
  const resolveGovernedRoute = (navId: ParentPortalNavId): string | null =>
    availableNavItems.has(navId) ? PARENT_PORTAL_ROUTE_MAP[navId] : null;
  const fallbackBaseRoute = resolveGovernedParentPortalFallbackRoute(
    new Set(Array.from(availableNavItems.keys())),
  ) ?? PARENT_PORTAL_ROUTE_MAP.applications;
  const fallbackNotificationRoute =
    `${fallbackBaseRoute}?notificationId=${encodedNotificationId}`;

  const isCalendarNotification = subject.includes('calendar')
    || subject.includes('interview')
    || subject.includes('schedule')
    || subject.includes('visit');

  if (isCalendarNotification) {
    const calendarRoute = resolveGovernedRoute('calendar');
    if (calendarRoute) {
      const params = new URLSearchParams({ notificationId: item.id });
      if (subject.includes('interview')) params.set('eventType', 'interview');
      else if (subject.includes('visit')) params.set('eventType', 'visit');
      if (applicationId) params.set('applicationId', applicationId);
      return `${calendarRoute}?${params.toString()}`;
    }
    return fallbackNotificationRoute;
  }

  if (applicationId) {
    return resolveGovernedRoute('applications')
      ? `/parent/portal--new/applications?applicationId=${encodeURIComponent(applicationId)}`
      : fallbackNotificationRoute;
  }
  if (subject.includes('attendance')) {
    return resolveGovernedRoute('attendance') ?? fallbackNotificationRoute;
  }
  if (subject.includes('academic') || subject.includes('grade') || subject.includes('report card')) {
    return resolveGovernedRoute('academic') ?? fallbackNotificationRoute;
  }
  if (
    subject.includes('tuition')
    || subject.includes('payment')
    || subject.includes('invoice')
    || subject.includes('bill')
  ) {
    return resolveGovernedRoute('tuition') ?? fallbackNotificationRoute;
  }
  if (
    subject.includes('financial aid')
    || subject.includes('scholarship')
    || subject.includes('appeal')
    || subject.includes('award')
  ) {
    return resolveGovernedRoute('financial-aid') ?? fallbackNotificationRoute;
  }
  if (subject.includes('enroll')) {
    return resolveGovernedRoute('enrollment') ?? fallbackNotificationRoute;
  }
  if (subject.includes('resource') || subject.includes('document')) {
    return resolveGovernedRoute('resources') ?? fallbackNotificationRoute;
  }

  return fallbackNotificationRoute;
};

export const ParentAppShell: React.FC<ParentAppShellProps> = ({
  title = 'Parent Portal',
  subtitle = 'Yeshiva Shaarei Tzion',
  showHeader = false,
  showHeaderTitle,
  showHeaderActions = false,
  showAssistant = false,
  showSecondaryNav = false,
  headerPolicyReason,
  contentInsetProfile,
  navOpen = false,
  assistantOpen = false,
  navItems = PARENT_PORTAL_NAV_ITEMS,
  secondaryNavItems = [],
  settingsItem,
  secondaryNavLabel = 'Temp',
  activeNavId = 'dashboard',
  contentVariant = 'bleed',
  status,
  assistant,
  children,
  adminChromeBanner,
  headerAdminChrome,
  userCardAction,
  onNavChange,
  onNavOpenChange,
  onAssistantOpenChange,
  onNotificationSelect,
  onLogout,
  notificationMenuItems,
  notificationUnreadCount,
  notificationIsLoading,
  notificationError,
}) => {
  const assistantPolicy = getAssistantRuntimePolicy({
    pathname: typeof window === 'undefined' ? '/parent/portal' : window.location.pathname,
    persona: 'parent',
  });
  const effectiveShowAssistant = showAssistant && assistantPolicy.enabled;

  const [navPinnedOpen, setNavPinnedOpen] = useState(navOpen);
  const [assistantExpanded, setAssistantExpanded] = useState(() => (
    effectiveShowAssistant ? assistantOpen : false
  ));
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sidebarFocusWithin, setSidebarFocusWithin] = useState(false);
  const [sidebarPointerDown, setSidebarPointerDown] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const logoutStateRef = useRef({ inFlight: false });
  const notificationSelectionPendingRef = useRef(false);
  const headerButtonsRef = useRef<HTMLDivElement | null>(null);
  const [userProfile, setUserProfile] = useState<CognitoProfile | null>(() => resolveCognitoProfile());
  const { enabled: nightModeEnabled, toggle: toggleNightMode } = useYstnjNightMode({ profile: userProfile });

  useEffect(() => {
    setNavPinnedOpen(navOpen);
  }, [navOpen]);
  useEffect(() => {
    setAssistantExpanded(effectiveShowAssistant ? assistantOpen : false);
  }, [assistantOpen, effectiveShowAssistant]);
  useEffect(() => {
    if (!sidebarPointerDown || typeof window === 'undefined') {
      return undefined;
    }
    const clearPointerDown = () => setSidebarPointerDown(false);
    window.addEventListener('pointerup', clearPointerDown);
    window.addEventListener('pointercancel', clearPointerDown);
    window.addEventListener('blur', clearPointerDown);
    return () => {
      window.removeEventListener('pointerup', clearPointerDown);
      window.removeEventListener('pointercancel', clearPointerDown);
      window.removeEventListener('blur', clearPointerDown);
    };
  }, [sidebarPointerDown]);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const media = window.matchMedia('(max-width: 720px)');
    const syncIsMobileViewport = () => setIsMobileViewport(media.matches);
    syncIsMobileViewport();
    media.addEventListener('change', syncIsMobileViewport);
    return () => media.removeEventListener('change', syncIsMobileViewport);
  }, []);

  const isBleed = contentVariant === 'bleed';
  const effectiveShowHeaderTitle = showHeaderTitle ?? showHeader;
  const shouldRenderHeader = effectiveShowHeaderTitle || showHeaderActions;
  const effectiveAssistantExpanded = assistantExpanded;
  const assistantVisible = effectiveShowAssistant && effectiveAssistantExpanded;
  const handleLogout = useCallback(async () => {
    await runBlockingLogout({
      action: onLogout,
      state: logoutStateRef.current,
      onPendingChange: setLogoutPending,
    });
  }, [onLogout]);

  const navigateToRoute = useCallback((route: string) => {
    if (typeof window === 'undefined') return;
    window.location.assign(route);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    let attempts = 0;
    const refreshProfile = () => {
      attempts += 1;
      const nextProfile = resolveCognitoProfile();
      setUserProfile((currentProfile) => (
        areCognitoProfilesEquivalent(currentProfile, nextProfile)
          ? currentProfile
          : nextProfile
      ));
      if ((nextProfile?.email && nextProfile?.name) || attempts >= 8) {
        window.clearInterval(intervalId);
      }
    };

    const intervalId = window.setInterval(refreshProfile, 1500);
    refreshProfile();

    const handleStorage = (event: StorageEvent) => {
      if (!event.key) return;
      if (event.key.endsWith('.idToken') || event.key.includes('triedge_persona')) {
        refreshProfile();
      }
    };
    const handleSessionUpdated = () => {
      refreshProfile();
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('ystnj-auth-session-updated', handleSessionUpdated);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('ystnj-auth-session-updated', handleSessionUpdated);
    };
  }, []);

  const userDisplayName = userProfile?.name || userProfile?.email || 'Signed In User';
  const userDisplayEmail = userProfile?.email || '';
  const showNightModeToggle = canUseShellThemeToggle(userProfile);
  const userRoleLabel = resolveAppShellPersonaLabel(
    userProfile?.persona ?? 'guest',
    'Parent',
    { isSuperAdmin: userProfile?.isSuperAdmin === true },
  );
  const normalizedSubtitle = subtitle?.trim() ?? '';
  const userPortalLabel = (
    normalizedSubtitle && !normalizedSubtitle.startsWith('/')
      ? normalizedSubtitle
      : (userRoleLabel === 'Parent' ? 'Parent Portal' : userRoleLabel)
  );
  const userAvatarLabel = getInitials(userDisplayName);
  const notifications = useNotificationsCenter({
    enabled: showHeaderActions,
    participantId: userProfile?.userSub,
  });
  const mappedNotificationMenuItems = useMemo(
    () => mapCrossHeaderNotificationsToMenuItems(notifications.items),
    [notifications.items]
  );
  const hasNotificationOverrides = notificationMenuItems !== undefined
    || notificationUnreadCount !== undefined
    || notificationIsLoading !== undefined
    || notificationError !== undefined;
  const effectiveNotificationMenuItems = notificationMenuItems ?? mappedNotificationMenuItems;
  const effectiveNotificationUnreadCount = notificationUnreadCount ?? notifications.unreadCount;
  const effectiveNotificationIsLoading = notificationIsLoading ?? notifications.isLoading;
  const effectiveNotificationError = notificationError ?? notifications.error;
  const navExpanded = isMobileViewport
    ? navPinnedOpen
    : (navPinnedOpen || sidebarHovered || sidebarFocusWithin || sidebarPointerDown);
  const navItemsById = useMemo(
    () => new Map(navItems.map((item) => [String(item.id), item] as const)),
    [navItems],
  );
  const handleNotificationSelect = useCallback((item: NotificationMenuItem) => {
    if (notificationSelectionPendingRef.current) return;
    notificationSelectionPendingRef.current = true;
    setNotificationsOpen(false);

    void runSafeNotificationNavigation(async () => {
      if (!hasNotificationOverrides) {
        try {
          await notifications.markNotificationRead(item.id);
        } catch (error) {
          console.warn('[ParentAppShell] Failed to mark notification read before navigation', error);
        }
      }
      const route = resolveParentNotificationRoute(item, navItemsById);
      if (onNotificationSelect) {
        await onNotificationSelect(item.id, route);
      } else if (route) {
        navigateToRoute(route);
      }
    }, '[ParentAppShell] Notification navigation failed').finally(() => {
      notificationSelectionPendingRef.current = false;
    });
  }, [hasNotificationOverrides, navItemsById, navigateToRoute, notifications, onNotificationSelect]);
  const primaryNavItems = useMemo(
    () => (
      PRIMARY_PARENT_PORTAL_NAV_ORDER
        .map((id) => navItemsById.get(id))
        .filter((item): item is SettingsNavItem => Boolean(item))
    ),
    [navItemsById],
  );
  const legacySecondaryNavItems = useMemo(
    () => (
      showSecondaryNav
        ? navItems.filter((item) => !PRIMARY_PARENT_PORTAL_NAV_IDS.has(String(item.id)))
        : []
    ),
    [navItems, showSecondaryNav],
  );
  const effectiveSecondaryNavItems = secondaryNavItems.length > 0
    ? secondaryNavItems
    : legacySecondaryNavItems;
  const hasActiveSecondaryNavItem = useMemo(
    () => effectiveSecondaryNavItems.some((item) => item.id === activeNavId),
    [activeNavId, effectiveSecondaryNavItems],
  );
  const tempNavDisclosure = useTempNavDisclosure({
    storageKey: PARENT_TEMP_NAV_DISCLOSURE_STORAGE_KEY,
    forceOpen: hasActiveSecondaryNavItem,
  });
  const resolvedHeaderTitle = useMemo(() => {
    const normalizedTitle = String(title ?? '').trim();
    if (normalizedTitle && normalizedTitle !== 'Portal' && normalizedTitle !== 'Parent Portal') {
      return normalizedTitle;
    }
    const routeTitle = PARENT_PORTAL_TITLE_BY_NAV_ID[activeNavId as ParentPortalNavId];
    return routeTitle || 'Applications';
  }, [activeNavId, title]);

  const handleNavToggle = () => {
    const next = !navPinnedOpen;
    setNavPinnedOpen(next);
    onNavOpenChange?.(next);
  };

  const closeMobileNavIfNeeded = () => {
    if (!isMobileViewport || !navPinnedOpen) return;
    setNavPinnedOpen(false);
    onNavOpenChange?.(false);
  };

  const handleNavItemSelect = (id: string) => {
    onNavChange?.(id);
    closeMobileNavIfNeeded();
  };

  const defaultAssistant = effectiveShowAssistant ? (
    <div
      id="admissions-ai-overlay"
      data-ai-layout="sidebar"
      data-testid="parent-shell-ai-overlay-mount"
    />
  ) : null;

  function handleAssistantToggle() {
    if (!effectiveShowAssistant) {
      return;
    }
    const next = !assistantExpanded;
    setAssistantExpanded(next);
    onAssistantOpenChange?.(next);
  }

  useEffect(() => {
    if (!effectiveShowAssistant || typeof window === 'undefined') {
      return () => {};
    }
    const handleOverlayCloseRequest = () => {
      if (!assistantExpanded) {
        return;
      }
      setAssistantExpanded(false);
      onAssistantOpenChange?.(false);
    };
    window.addEventListener(ASSISTANT_OVERLAY_REQUEST_CLOSE_EVENT, handleOverlayCloseRequest as EventListener);
    return () => {
      window.removeEventListener(ASSISTANT_OVERLAY_REQUEST_CLOSE_EVENT, handleOverlayCloseRequest as EventListener);
    };
  }, [assistantExpanded, effectiveShowAssistant, onAssistantOpenChange]);

  useEffect(() => {
    if (!effectiveShowAssistant) {
      return;
    }
    emitAssistantShellState({
      expanded: effectiveAssistantExpanded,
      source: 'parent-app-shell',
    });
  }, [effectiveAssistantExpanded, effectiveShowAssistant]);

  useEffect(() => {
    if (!notificationsOpen) {
      return undefined;
    }
    const handleDocumentMouseDown = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const panel = document.querySelector('[data-testid="parent-shell-notifications-panel"]');
      if (panel?.contains(targetNode)) {
        return;
      }
      if (!headerButtonsRef.current?.contains(targetNode)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [notificationsOpen]);

  const resolvedAssistant = effectiveShowAssistant
    ? (assistant === undefined ? defaultAssistant : assistant)
    : null;
  const contentInsetClass = contentInsetProfile === 'parent-financial-aid-parity-v1'
    ? styles.contentInsetParentFinancialAidParity
    : '';
  const mainInsetClass = contentInsetProfile === 'parent-financial-aid-parity-v1'
    ? styles.mainInsetParentFinancialAidParity
    : contentInsetProfile === 'parent-mobile-zero-main-inset-v1'
      ? styles.mainInsetParentMobileZero
      : '';

  return (
    <div
      className={`${styles.shell} ${navExpanded ? styles.shellNavExpanded : ''}`}
      data-shared-shell-theme={nightModeEnabled ? 'dark' : 'light'}
      data-shell-render-policy="app-filter"
      data-extracted-from="owned:ystnj/shell/parent-shell-root"
      data-triedge-wrapper-label={TRIEDGE_WRAPPER_LABELS.shellRoot}
      data-triedge-wrapper-intent="compose_parent_shell_around_route_content"
      data-triedge-wrapper-status={OVERRIDE_GOVERNANCE_REGISTRY.wrapper_shell_mount_root.status}
      data-triedge-wrapper-action={OVERRIDE_GOVERNANCE_REGISTRY.wrapper_shell_mount_root.plannedAction}
      data-shell-header-policy-reason={headerPolicyReason ?? 'unspecified'}
      data-triedge-header-policy-label={
        headerPolicyReason === 'enforced_parent_financial_aid_title_for_route_consistency_2026_02_19'
          ? TRIEDGE_OVERRIDE_LABELS.parentFinancialAidHeaderPolicy
          : TRIEDGE_OVERRIDE_LABELS.defaultHeaderPolicy
      }
    >
      {/* Extracted from `apps/ystnj/storybook-assets/external-ui/intellio-v2---portal---shell/components/Sidebar.tsx` and mapped to owned routing IDs. */}
      <aside
        className={`${styles.sidebar} ${navExpanded ? styles.sidebarOpen : styles.sidebarCollapsed}`}
        aria-label="Parent portal navigation"
        data-testid="parent-shell-sidebar"
        data-test-id="parent-shell-sidebar"
        data-expanded={navExpanded ? 'true' : 'false'}
        data-extracted-from="owned:ystnj/shell/parent-sidebar:v2-source-aligned"
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
        onFocusCapture={() => setSidebarFocusWithin(true)}
        onBlurCapture={(event) => {
          const nextTarget = event.relatedTarget as Node | null;
          if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
            setSidebarFocusWithin(false);
          }
        }}
        onPointerDownCapture={() => setSidebarPointerDown(true)}
      >
        <div className={styles.sidebarHeader}>
        <div className={styles.userMeta}>
          <div className={styles.userAvatar} data-testid="parent-shell-user-avatar-header">
            {userAvatarLabel}
          </div>
          {userCardAction ? (
            <div className={styles.userCardAction} data-testid="parent-shell-user-card-action">
              {userCardAction}
            </div>
          ) : null}
          <div
            className={`${styles.userDetails} ${styles.sidebarExpandable}`}
            data-extracted-from="owned:ystnj/shell/parent-sidebar-user-meta"
            >
              <div className={styles.userName} data-testid="parent-shell-user-name">
                {userDisplayName}
              </div>
              <div className={styles.userRole} data-testid="parent-shell-user-role">
                {userPortalLabel}
              </div>
              {userDisplayEmail ? (
                <div className={styles.userEmail} data-testid="parent-shell-user-email">
                  {userDisplayEmail}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className={styles.sidebarBody}>
          <nav
            className={`${styles.navList} ${showSecondaryNav ? '' : styles.navListCompact}`.trim()}
            data-testid="parent-shell-nav-list"
            data-extracted-from="owned:ystnj/shell/parent-nav-list"
          >
            {primaryNavItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`${styles.navItem} ${activeNavId === item.id ? styles.navItemActive : ''}`}
                onClick={() => handleNavItemSelect(item.id)}
                data-testid={`parent-shell-nav-item-${item.id}`}
                aria-label={item.label}
              >
                <span className={styles.navIcon} data-testid={`parent-shell-nav-icon-${item.id}`}>
                  <item.icon className={styles.iconMd} />
                </span>
                <span className={`${styles.navLabel} ${styles.sidebarExpandable}`}>{item.label}</span>
              </button>
            ))}
          </nav>
          {effectiveSecondaryNavItems.length ? (
            <section
              className={`${styles.sidebarSection} ${!tempNavDisclosure.expanded ? styles.sidebarSectionCollapsed : ''}`}
              aria-label={secondaryNavLabel}
              data-testid="parent-shell-secondary-section"
            >
              <button
                type="button"
                className={styles.sectionToggle}
                onClick={tempNavDisclosure.toggle}
                aria-expanded={tempNavDisclosure.expanded}
                aria-controls="parent-shell-secondary-panel"
                aria-disabled={tempNavDisclosure.lockedOpen ? 'true' : undefined}
                data-testid="parent-shell-secondary-toggle"
              >
                <span className={`${styles.sectionToggleLabel} ${styles.sidebarExpandable}`}>{secondaryNavLabel}</span>
                <ChevronRight
                  className={`${styles.sectionToggleIcon} ${tempNavDisclosure.expanded ? styles.sectionToggleIconExpanded : ''}`}
                />
              </button>
              {tempNavDisclosure.expanded ? (
                <div
                  id="parent-shell-secondary-panel"
                  className={styles.secondaryNavPanel}
                  data-testid="parent-shell-secondary-panel"
                >
                  <div className={styles.secondaryNavList} data-testid="parent-shell-secondary-nav-list">
                    {effectiveSecondaryNavItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`${styles.navItem} ${activeNavId === item.id ? styles.navItemActive : ''}`}
                        onClick={() => handleNavItemSelect(item.id)}
                        data-testid={`parent-shell-nav-item-${item.id}`}
                        aria-label={item.label}
                      >
                        <span className={styles.navIcon} data-testid={`parent-shell-nav-icon-${item.id}`}>
                          <item.icon className={styles.iconMd} />
                        </span>
                        <span className={`${styles.navLabel} ${styles.sidebarExpandable}`}>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}
        </div>
        <div className={styles.sidebarBottom} data-extracted-from="owned:ystnj/shell/parent-sidebar-footer">
          <div className={styles.sidebarFooter}>
            <div className={`${styles.systemLabel} ${styles.sidebarExpandable}`}>System</div>
            {settingsItem ? (
              <div className={styles.navFooter}>
                <button
                  type="button"
                  className={`${styles.navItem} ${activeNavId === settingsItem.id ? styles.navItemActive : ''}`}
                  onClick={() => handleNavItemSelect(settingsItem.id)}
                  data-testid="parent-shell-settings-item"
                  aria-label={settingsItem.label}
                >
                  <span className={styles.navIcon} data-testid="parent-shell-settings-icon">
                    <settingsItem.icon className={styles.iconMd} />
                  </span>
                  <span className={`${styles.navLabel} ${styles.sidebarExpandable}`}>{settingsItem.label}</span>
                </button>
              </div>
            ) : null}
            <button
              className={`${styles.userAction} g_id_signout`}
              type="button"
              onClick={handleLogout}
              disabled={logoutPending}
              aria-busy={logoutPending ? 'true' : undefined}
              data-testid="parent-shell-logout-item"
            >
              <span className={styles.navIcon} data-testid="parent-shell-logout-icon">
                <LogOut className={`${styles.iconMd} ${styles.logoutIconStable}`} />
              </span>
              <span className={`${styles.userActionLabel} ${styles.sidebarExpandable}`}>Log out</span>
            </button>
            <div className={`${styles.footerBrand} ${styles.sidebarExpandable}`}>
              <div className={styles.footerBrandTitle}>Intellio.</div>
              <div className={styles.footerBrandMeta}>© 2026 Intellio Labs, Inc.</div>
            </div>
          </div>
        </div>
      </aside>
      {isMobileViewport && navExpanded ? (
        <button
          type="button"
          className={styles.mobileNavBackdrop}
          aria-label="Close menu"
          onClick={handleNavToggle}
        />
      ) : null}

      <main
        className={`${styles.main} ${assistantVisible ? styles.mainWithAssistant : styles.mainNoAssistant} ${
          isBleed ? styles.mainBleed : styles.mainPanel
        } ${mainInsetClass}`.trim()}
        data-extracted-from="owned:ystnj/shell/parent-main"
      >
        {shouldRenderHeader ? (
          <header
            className={`${styles.header} ${effectiveShowHeaderTitle ? '' : styles.headerActionsOnly}`.trim()}
            data-testid="parent-shell-header"
            data-extracted-from="owned:ystnj/shell/parent-header"
          >
            {effectiveShowHeaderTitle ? (
              <div className={styles.headerMain}>
                <button
                  type="button"
                  className={styles.mobileMenuToggle}
                  data-testid="parent-shell-menu-toggle"
                  aria-label="Toggle Sidebar"
                  aria-expanded={navExpanded ? 'true' : 'false'}
                  onClick={handleNavToggle}
                >
                  {navExpanded ? (
                    <IconLayoutSidebarLeftCollapse
                      className={`${styles.mobileMenuIcon} lucide lucide-indent-decrease`}
                      data-testid="parent-shell-menu-toggle-icon-expanded"
                    />
                  ) : (
                    <IconLayoutSidebarLeftExpand
                      className={`${styles.mobileMenuIcon} lucide lucide-indent-increase`}
                      data-testid="parent-shell-menu-toggle-icon-collapsed"
                    />
                  )}
                </button>
                <h1 className={styles.headerTitle} data-testid="parent-shell-header-title">
                  {resolvedHeaderTitle}
                </h1>
              </div>
            ) : null}
            <div className={styles.headerActions}>
              {headerAdminChrome}
              {status ? <div className={styles.status}>{status}</div> : null}
              {showHeaderActions ? (
                <div ref={headerButtonsRef} className={styles.headerButtons}>
                  {effectiveShowAssistant ? (
                    <CrossHeaderActions
                      testIdPrefix="parent-shell"
                      assistantExpanded={effectiveAssistantExpanded}
                      notificationsOpen={notificationsOpen}
                      unreadCount={notifications.unreadCount}
                      onAssistantToggle={handleAssistantToggle}
                      onNotificationsToggle={() => setNotificationsOpen((open) => !open)}
                      middleSlot={showNightModeToggle
                        ? (
                            <ShellThemeToggle
                              enabled={nightModeEnabled}
                              onToggle={toggleNightMode}
                              testId="parent-shell-header-settings-toggle"
                              testIdAlias="parent-shell-header-night-mode-toggle"
                            />
                          )
                        : undefined}
                    />
                  ) : (
                    <div className={styles.parentHeaderActionGroup}>
                      <button
                        className={`${styles.headerIconButton} ${notificationsOpen ? styles.headerIconButtonActive : ''}`}
                        type="button"
                        onClick={() => setNotificationsOpen((open) => !open)}
                        data-testid="parent-shell-notifications-toggle"
                        aria-expanded={notificationsOpen}
                        aria-label="Toggle notifications"
                        title="Notifications"
                      >
                        <Bell className={styles.headerIcon} />
                        {effectiveNotificationUnreadCount > 0 ? (
                          <span
                            className={styles.headerNotificationBadge}
                            data-testid="parent-shell-notifications-badge"
                            aria-label={`${effectiveNotificationUnreadCount} unread notifications`}
                            title={`${effectiveNotificationUnreadCount} unread notifications`}
                          />
                        ) : null}
                      </button>
                      {showNightModeToggle ? (
                        <ShellThemeToggle
                          enabled={nightModeEnabled}
                          onToggle={toggleNightMode}
                          testId="parent-shell-header-settings-toggle"
                          testIdAlias="parent-shell-header-night-mode-toggle"
                        />
                      ) : null}
                    </div>
                  )}
                  <CrossHeaderNotificationsMenu
                    testIdPrefix="parent-shell"
                    open={notificationsOpen}
                    unreadCount={effectiveNotificationUnreadCount}
                    items={effectiveNotificationMenuItems}
                    isLoading={effectiveNotificationIsLoading}
                    error={effectiveNotificationError}
                    onClose={() => setNotificationsOpen(false)}
                    onSelectItem={handleNotificationSelect}
                  />
                </div>
              ) : null}
            </div>
          </header>
        ) : null}

        {adminChromeBanner}

        <section
          className={`${styles.content} ${contentInsetClass}`.trim()}
          data-testid="parent-shell-content-root"
          data-extracted-from="owned:ystnj/shell/parent-content"
          data-shell-content-min-inset="16"
          data-shell-content-inset-profile={contentInsetProfile ?? 'default'}
          data-triedge-content-inset-label={
            contentInsetProfile === 'parent-financial-aid-parity-v1'
              ? TRIEDGE_OVERRIDE_LABELS.parentFinancialAidContentInset
              : TRIEDGE_OVERRIDE_LABELS.defaultContentInset
          }
        >
          {children}
        </section>
      </main>

      {effectiveShowAssistant ? (
        <aside
          className={`${styles.assistant} ${effectiveAssistantExpanded ? styles.assistantOpen : styles.assistantClosed}`}
          aria-label="Intellio assistant"
          data-testid="parent-shell-assistant"
          data-expanded={effectiveAssistantExpanded ? 'true' : 'false'}
          data-extracted-from="owned:ystnj/shell/parent-assistant"
        >
          <div className={styles.assistantBody}>
            {resolvedAssistant ? (
              <div
                className={styles.assistantMount}
                data-testid="intellio-assistant"
                data-extracted-from="owned:ystnj/shell/parent-assistant-mount"
              >
                {resolvedAssistant}
              </div>
            ) : null}
          </div>
        </aside>
      ) : null}
    </div>
  );
};
